import { useRef } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Printer, GridFour } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { usePDFExport } from "@/utils/pdfExport";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05 } } };
const itemAnim = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const CONTENT = {
  en: {
    page_title: "The Bayer Filter — How Cameras See Color",
    page_sub: "Your camera sensor is colorblind. Every photo you've ever taken in color exists because of a microscopic checkerboard of red, green, and blue invented by Bryce Bayer at Kodak in 1976. This is the single most important piece of technology most photographers never learn.",
    download: "Download as PDF",
    generating: "Generating...",

    problem_title: "The problem — silicon only sees brightness",
    problem_body: "A camera sensor is a grid of millions of photosites (think of them as light buckets). Each one counts how many photons land in it and outputs a number for BRIGHTNESS. That's it. A bare sensor produces a grayscale image — it has no idea whether the light was red, green, or blue. To make a color photograph, the camera has to trick a colorblind chip into recording color.",

    solution_title: "The solution — a grid of tiny color filters",
    solution_body: "Bayer's answer: glue a microscopic color filter on top of every single photosite. Each filter only lets ONE color of light through. Arrange them in a repeating 2×2 tile — one Red, one Blue, and TWO Greens — and you get the Bayer Color Filter Array (CFA). The pattern is written 'RGGB'.",

    why_green_title: "Why twice as much green?",
    why_green_body: "Human eyes have far more receptors for green than for red or blue — green is the color we resolve detail and brightness in most sharply. Bayer matched the sensor to the eye: 50% green, 25% red, 25% blue. This makes the final image look sharp and natural to us, because the camera puts its 'resolution budget' where our vision is most sensitive.",

    bayer_caption: "The repeating Bayer tile. Notice: two greens for every one red and one blue.",

    demosaic_title: "Demosaicing — filling in the missing colors",
    demosaic_body: "Here's the clever part. After the exposure, every pixel knows only ONE of its three colors (the rest were blocked by its filter). The image processor then runs an algorithm called demosaicing (or 'debayering'): for each pixel it intelligently estimates the two missing color values by averaging its neighbors. A red pixel borrows green and blue from the pixels around it. Multiply that across 24 million pixels, dozens of times per second, and you get a full-color image in real time.",
    demosaic_steps: [
      "Light passes through each tiny color filter; each photosite records ONE color value.",
      "The raw mosaic looks like a colored checkerboard — incomplete.",
      "Demosaicing estimates the 2 missing channels per pixel from neighboring photosites.",
      "Result: a complete RGB value for every pixel — a normal color photo.",
    ],

    raw_title: "Why this makes RAW so powerful",
    raw_body: "A JPEG bakes the demosaic + white balance + contrast permanently into the file. A RAW file saves the ORIGINAL single-color mosaic data straight off the sensor, before any guessing. That's why RAW gives you so much editing latitude: you can re-do the white balance, recover clipped highlights, and demosaic with better software later — because the camera's first guess isn't locked in.",

    alt_title: "The alternatives (and why they exist)",
    alt: [
      "Fujifilm X-Trans: a larger, more random 6×6 pattern. Its irregular layout reduces moiré (false color patterns on fine repeating detail) so Fuji can skip the image-softening low-pass filter — yielding crisp results.",
      "Sigma Foveon: stacks three light-sensitive layers (R, G, B) at every pixel location, so each pixel captures all three colors directly. No demosaicing, extremely fine color detail — but trickier in low light.",
      "Monochrome sensors (e.g. Leica M Monochrom): remove the color filter entirely. With no filters blocking light and no demosaicing, they capture stunningly sharp, low-noise black-and-white images.",
    ],

    moire_title: "Moiré & the anti-aliasing filter",
    moire_body: "Because the Bayer grid samples color at regular intervals, very fine repeating patterns (a tweed jacket, a distant brick wall, a TV screen) can clash with the grid and produce rainbow 'moiré' artifacts. Many cameras add a low-pass (anti-aliasing) filter to slightly blur the image and prevent this. High-resolution bodies often drop it — there's enough pixel density that moiré rarely shows, and you gain maximum sharpness.",

    takeaway_title: "The takeaway",
    takeaway_body: "Every color photo is a reconstruction. The sensor measures brightness through a red-green-blue mosaic, and a processor reverse-engineers the full color of each pixel. Understanding this demystifies RAW vs JPEG, explains why green channels are the cleanest, and shows why sensor and processor quality matter as much as the lens.",

    related_more: "Part of the camera's inner workings — see also:",
    related_link: "The Anatomy of the DSLR & Mirrorless",

    pro_tip: "Pro tip: shoot RAW whenever color accuracy matters. You're preserving the sensor's pure mosaic data and keeping every demosaicing and white-balance decision editable — instead of letting the camera lock in its first guess.",
  },

  es: {
    page_title: "El Filtro Bayer — Como las Camaras Ven el Color",
    page_sub: "El sensor de tu camara es daltonico. Cada foto a color que has tomado existe gracias a un tablero microscopico de rojo, verde y azul inventado por Bryce Bayer en Kodak en 1976. Es la pieza de tecnologia mas importante que la mayoria de fotografos nunca aprende.",
    download: "Descargar como PDF",
    generating: "Generando...",

    problem_title: "El problema — el silicio solo ve brillo",
    problem_body: "Un sensor de camara es una rejilla de millones de fotositos (imaginalos como cubetas de luz). Cada uno cuenta cuantos fotones caen en el y entrega un numero de BRILLO. Eso es todo. Un sensor desnudo produce una imagen en escala de grises — no tiene idea si la luz era roja, verde o azul. Para hacer una foto a color, la camara debe engañar a un chip daltonico para que registre color.",

    solution_title: "La solucion — una rejilla de filtros de color diminutos",
    solution_body: "La respuesta de Bayer: pegar un filtro de color microscopico sobre cada fotosito. Cada filtro solo deja pasar UN color de luz. Acomodalos en un mosaico repetido de 2×2 — uno Rojo, uno Azul y DOS Verdes — y obtienes el Arreglo de Filtros de Color Bayer (CFA). El patron se escribe 'RGGB'.",

    why_green_title: "¿Por que el doble de verde?",
    why_green_body: "El ojo humano tiene muchos mas receptores para el verde que para el rojo o el azul — el verde es el color donde resolvemos detalle y brillo con mayor nitidez. Bayer ajusto el sensor al ojo: 50% verde, 25% rojo, 25% azul. Esto hace que la imagen final se vea nitida y natural para nosotros, porque la camara pone su 'presupuesto de resolucion' donde nuestra vision es mas sensible.",

    bayer_caption: "El mosaico Bayer repetido. Nota: dos verdes por cada rojo y azul.",

    demosaic_title: "Demosaicing — rellenando los colores faltantes",
    demosaic_body: "Aqui esta lo ingenioso. Despues de la exposicion, cada pixel conoce solo UNO de sus tres colores (los demas fueron bloqueados por su filtro). El procesador ejecuta un algoritmo llamado demosaicing (o 'debayering'): para cada pixel estima inteligentemente los dos valores de color faltantes promediando a sus vecinos. Un pixel rojo toma prestado verde y azul de los pixeles a su alrededor. Multiplica eso por 24 millones de pixeles, decenas de veces por segundo, y obtienes una imagen a todo color en tiempo real.",
    demosaic_steps: [
      "La luz pasa por cada filtro de color diminuto; cada fotosito registra UN valor de color.",
      "El mosaico bruto parece un tablero de colores — incompleto.",
      "El demosaicing estima los 2 canales faltantes por pixel a partir de los fotositos vecinos.",
      "Resultado: un valor RGB completo para cada pixel — una foto a color normal.",
    ],

    raw_title: "Por que esto hace al RAW tan potente",
    raw_body: "Un JPEG graba el demosaic + balance de blancos + contraste permanentemente en el archivo. Un archivo RAW guarda los datos ORIGINALES del mosaico de un solo color directo del sensor, antes de cualquier estimacion. Por eso el RAW te da tanta libertad de edicion: puedes rehacer el balance de blancos, recuperar altas luces quemadas y hacer el demosaicing con mejor software despues — porque la primera estimacion de la camara no quedo fija.",

    alt_title: "Las alternativas (y por que existen)",
    alt: [
      "Fujifilm X-Trans: un patron mas grande y aleatorio de 6×6. Su disposicion irregular reduce el moire (patrones de color falso en detalles finos repetidos) para que Fuji pueda omitir el filtro paso-bajo que suaviza la imagen — logrando resultados nitidos.",
      "Sigma Foveon: apila tres capas sensibles a la luz (R, G, B) en cada pixel, asi cada pixel capta los tres colores directamente. Sin demosaicing, detalle de color extremadamente fino — pero mas dificil en poca luz.",
      "Sensores monocromaticos (ej. Leica M Monochrom): eliminan el filtro de color por completo. Sin filtros bloqueando luz y sin demosaicing, capturan imagenes en blanco y negro asombrosamente nitidas y con poco ruido.",
    ],

    moire_title: "Moire y el filtro anti-aliasing",
    moire_body: "Como la rejilla Bayer muestrea el color a intervalos regulares, los patrones finos muy repetidos (un saco de tweed, una pared de ladrillos lejana, una pantalla de TV) pueden chocar con la rejilla y producir artefactos 'moire' tipo arcoiris. Muchas camaras añaden un filtro paso-bajo (anti-aliasing) que difumina ligeramente la imagen para evitarlo. Los cuerpos de alta resolucion suelen quitarlo — hay suficiente densidad de pixeles para que el moire casi no aparezca, y ganas maxima nitidez.",

    takeaway_title: "La conclusion",
    takeaway_body: "Cada foto a color es una reconstruccion. El sensor mide brillo a traves de un mosaico rojo-verde-azul, y un procesador deduce el color completo de cada pixel. Entender esto desmitifica RAW vs JPEG, explica por que los canales verdes son los mas limpios, y muestra por que la calidad del sensor y el procesador importa tanto como el lente.",

    related_more: "Parte del funcionamiento interno de la camara — ve tambien:",
    related_link: "La Anatomia de la Camara DSLR y Mirrorless",

    pro_tip: "Tip pro: dispara en RAW siempre que la precision de color importe. Estas preservando los datos puros del mosaico del sensor y manteniendo editable cada decision de demosaicing y balance de blancos — en vez de dejar que la camara fije su primera estimacion.",
  },
};

function BigBayerGrid() {
  const R = "#EF4444", G = "#22C55E", B = "#3B82F6";
  const tile = [[R, G], [G, B]];
  const size = 6;
  const cell = 30;
  const rows = [];
  for (let r = 0; r < size; r++) {
    for (let cI = 0; cI < size; cI++) {
      const color = tile[r % 2][cI % 2];
      rows.push(
        <rect key={`${r}-${cI}`} x={20 + cI * cell} y={20 + r * cell} width={cell - 2} height={cell - 2} fill={color} opacity="0.85" rx="3" />
      );
    }
  }
  return (
    <svg viewBox="0 0 220 220" className="w-full max-w-[220px] h-auto mx-auto" role="img" aria-label="Bayer RGGB pattern" data-testid="bayer-grid-svg">
      <rect x="0" y="0" width="220" height="220" fill="#0A0A0A" rx="8" />
      {rows}
    </svg>
  );
}

export default function BayerFilterPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(printRef, () => `bayer-filter-${lang}.pdf`, [lang]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black" data-testid="bayer-page">
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2 flex items-center gap-3">
                <GridFour weight="fill" className="w-8 h-8 text-[#F5A524]" />
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button onClick={download} disabled={!ready} className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60" data-testid="bayer-download-btn">
              <Printer className="w-4 h-4 mr-1.5" />{generating ? c.generating : c.download}
            </Button>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-rose-950/30 backdrop-blur-md border border-rose-500/40 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.problem_title}</h2>
            <p className="text-[#D4D4D8] text-sm leading-relaxed">{c.problem_body}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.solution_title}</h2>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.solution_body}</p>
              </div>
              <div>
                <BigBayerGrid />
                <p className="text-[#71717A] text-xs italic mt-2 text-center leading-relaxed">{c.bayer_caption}</p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-emerald-950/20 backdrop-blur-md border border-emerald-500/30 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.why_green_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.why_green_body}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.demosaic_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.demosaic_body}</p>
            <ol className="space-y-2">
              {c.demosaic_steps.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/30 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.raw_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.raw_body}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.alt_title}</h2>
            <ul className="space-y-2">{c.alt.map((a, i) => (<li key={i} className="flex items-start gap-2"><span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" /><span className="text-[#A1A1AA] text-sm leading-relaxed">{a}</span></li>))}</ul>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.moire_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.moire_body}</p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-2">{c.takeaway_title}</h2>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.takeaway_body}</p>
            <p className="text-[#71717A] text-sm">
              {c.related_more}{" "}
              <Link to="/anatomy" className="text-[#F5A524] hover:underline font-medium" data-testid="bayer-anatomy-link">
                {c.related_link} →
              </Link>
            </p>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-xl p-4">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="bayer-print">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-rose-200 pb-2 text-rose-700">1. {c.problem_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.problem_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-amber-200 pb-2 text-amber-700">2. {c.solution_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.solution_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-emerald-200 pb-2 text-emerald-700">3. {c.why_green_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.why_green_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.demosaic_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.demosaic_body}</p>
        <ol className="list-decimal ml-5 space-y-1 mb-4">{c.demosaic_steps.map((s, i) => (<li key={i} className="text-xs text-gray-700">{s}</li>))}</ol>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.raw_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.raw_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.alt_title}</h2>
        <ul className="list-disc ml-5 space-y-1 mb-4">{c.alt.map((a, i) => (<li key={i} className="text-xs text-gray-700">{a}</li>))}</ul>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.moire_title}</h2>
        <p className="text-sm text-gray-700 mb-4">{c.moire_body}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">8. {c.takeaway_title}</h2>
        <p className="text-sm text-gray-700 mb-3">{c.takeaway_body}</p>
        <p className="text-xs text-gray-700 italic"><strong>Pro tip:</strong> {c.pro_tip}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
